import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { listAllEmployees, peopleGateway } from '../../people';
import type { EmployeeResponse } from '../../people/types';
import { timeAndLeaveGateway } from '../index';
import { buildMonthlyAccrualRequest } from '../accrualPeriod';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { EscoHrTimeOffSection } from '../components/EscoHrTimeOffSection';
import {
  leavePlanTypeLabels, leaveRequestStatusLabels, LeaveRequestStatus,
  type HolidayResponse, type LeaveBalanceResponse, type LeavePlanEnrollmentResponse,
  type LeavePlanResponse, type LeaveRequestResponse,
} from '../types';

const SUB_TABS = [
  { key: 'requests', label: 'Requests' },
  { key: 'balances', label: 'Plans & Balances' },
  { key: 'holidays', label: 'Holidays' },
  { key: 'employee-experience', label: 'Employee Experience' },
] as const;
type SubTab = (typeof SUB_TABS)[number]['key'];

export function TimeOffPage() {
  const [subTab, setSubTab] = useState<SubTab>('requests');
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [plans, setPlans] = useState<LeavePlanResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // This picker needs the complete employee collection, not one page of it — follows every page
        // of the server-paginated Employee List query via the shared load-all helper.
        const [emp, planList] = await Promise.all([
          listAllEmployees(peopleGateway, { sortBy: 'name' }),
          timeAndLeaveGateway.listLeavePlans(),
        ]);
        if (cancelled) return;
        setEmployees(emp.filter((e) => e.isActive));
        setPlans(planList);
        const first = emp[0];
        if (first) setEmployeeId((prev) => prev || first.id);
      } catch (err) {
        if (!cancelled) setError(friendlyErrorMessage(err, 'Could not load employees or leave plans.'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
    <EscoHrTimeOffSection />
    <section className="card card--wide">
      <div className="section-header">
        <h2>Time Off</h2>
        <span className="badge badge--ok">Leave plans, requests, balances, holidays</span>
      </div>
      <p className="muted">
        Backed by <code>EPN.TimeAndLeave.Api</code> — see <code>docs/time-off.md</code> for what this does
        and does not model (no manual balance adjustment with a reason yet).
      </p>

      {error && <p className="error">{error}</p>}

      <label htmlFor="toff-employee-select">Employee</label>
      <select id="toff-employee-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
        {employees.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>)}
      </select>

      <div className="tabs" style={{ marginTop: 12 }}>
        {SUB_TABS.map((t) => (
          <button key={t.key} type="button" className={subTab === t.key ? 'tab tab--active' : 'tab'} onClick={() => setSubTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'requests' && employeeId && <RequestsPanel employeeId={employeeId} plans={plans} />}
      {subTab === 'balances' && employeeId && <BalancesPanel employeeId={employeeId} plans={plans} />}
      {subTab === 'holidays' && employeeId && <HolidaysPanel employeeId={employeeId} />}
      {subTab === 'employee-experience' && employeeId && (
        // key={employeeId}: switching the selected employee unmounts and remounts the panel, so no
        // employee-specific state (enrollments, balances, requests, errors, half-typed form values) can
        // survive into the next employee's view.
        <EmployeeExperiencePanel key={employeeId} employeeId={employeeId} plans={plans} employeeName={employeeDisplayName(employees.find((e) => e.id === employeeId))} />
      )}
    </section>
    </>
  );
}

function RequestsPanel({ employeeId, plans }: { employeeId: string; plans: LeavePlanResponse[] }) {
  const [requests, setRequests] = useState<LeaveRequestResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [leavePlanId, setLeavePlanId] = useState('');
  const [leaveDate, setLeaveDate] = useState('');
  const [hours, setHours] = useState('8');
  const [reason, setReason] = useState('');

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await timeAndLeaveGateway.listLeaveRequests(employeeId);
      setRequests(list.sort((a, b) => b.createdAtUtc.localeCompare(a.createdAtUtc)));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load leave requests.'));
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  useEffect(() => {
    const first = plans[0];
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (first) setLeavePlanId((prev) => prev || first.id);
  }, [plans]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await timeAndLeaveGateway.createLeaveRequest(employeeId, {
        leavePlanId, reason: reason || null, days: [{ leaveDate, hours: Number(hours) }],
      });
      setReason('');
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not submit the leave request.'));
    }
  }

  async function withBusy(id: string, action: () => Promise<unknown>, fallback: string) {
    setError(null);
    setBusyId(id);
    try {
      await action();
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, fallback));
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(e: FormEvent) {
    e.preventDefault();
    if (!rejectingId) return;
    await withBusy(rejectingId, () => timeAndLeaveGateway.rejectLeaveRequest(rejectingId, rejectionReason), 'Could not reject the leave request.');
    setRejectingId(null);
    setRejectionReason('');
  }

  return (
    <div>
      {error && <p className="error">{error}</p>}

      <form className="inline-form" onSubmit={handleSubmit} style={{ margin: '12px 0' }}>
        <select value={leavePlanId} onChange={(e) => setLeavePlanId(e.target.value)} required>
          {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({leavePlanTypeLabels[p.planType]})</option>)}
        </select>
        <label className="inline-form-date-label">
          Leave date
          <input type="date" value={leaveDate} onChange={(e) => setLeaveDate(e.target.value)} required />
        </label>
        <input type="number" step="0.25" min="0" placeholder="Hours" value={hours} onChange={(e) => setHours(e.target.value)} required />
        <input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button type="submit" disabled={!leavePlanId || !leaveDate}>Submit request</button>
      </form>

      {rejectingId && (
        <form className="inline-form" onSubmit={handleReject}>
          <strong>Rejection reason</strong>
          <input value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} required />
          <button type="submit">Confirm reject</button>
          <button type="button" className="link" onClick={() => setRejectingId(null)}>Cancel</button>
        </form>
      )}

      {loading && <p className="muted">Loading…</p>}

      {!loading && (
        <table>
          <thead>
            <tr><th>Days</th><th>Reason</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id}>
                <td>{r.days.map((d) => `${d.leaveDate} (${d.hours}h)`).join(', ')}</td>
                <td>{r.reason ?? '—'}</td>
                <td>
                  {leaveRequestStatusLabels[r.status]}
                  {r.status === LeaveRequestStatus.Rejected && r.rejectionReason && <div className="muted">Reason: {r.rejectionReason}</div>}
                </td>
                <td>
                  {r.status === LeaveRequestStatus.Submitted && (
                    <>
                      <button type="button" className="link" disabled={busyId === r.id} onClick={() => void withBusy(r.id, () => timeAndLeaveGateway.approveLeaveRequest(r.id), 'Could not approve the leave request.')}>Approve</button>
                      <button type="button" className="link" disabled={busyId === r.id} onClick={() => setRejectingId(r.id)}>Reject</button>
                      <button type="button" className="link" disabled={busyId === r.id} onClick={() => void withBusy(r.id, () => timeAndLeaveGateway.cancelLeaveRequest(r.id), 'Could not cancel the leave request.')}>Cancel</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && <tr><td colSpan={4} className="muted">No leave requests for this employee yet.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BalancesPanel({ employeeId, plans }: { employeeId: string; plans: LeavePlanResponse[] }) {
  const [leavePlanId, setLeavePlanId] = useState('');
  const [balance, setBalance] = useState<LeaveBalanceResponse | null>(null);
  const [enrollments, setEnrollments] = useState<LeavePlanEnrollmentResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const first = plans[0];
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (first) setLeavePlanId((prev) => prev || first.id);
  }, [plans]);

  const load = useCallback(async () => {
    if (!leavePlanId) return;
    setError(null);
    try {
      const [bal, enr] = await Promise.all([
        timeAndLeaveGateway.getLeaveBalance(employeeId, leavePlanId),
        timeAndLeaveGateway.listEnrollments(employeeId),
      ]);
      setBalance(bal);
      setEnrollments(enr);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load balance or enrollment.'));
    }
  }, [employeeId, leavePlanId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const isEnrolled = enrollments.some((e) => e.leavePlanId === leavePlanId && e.isActive);

  async function handleEnroll() {
    setBusy(true);
    setError(null);
    try {
      await timeAndLeaveGateway.enrollEmployee(employeeId, leavePlanId);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not enroll the employee.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleAccrue() {
    setBusy(true);
    setError(null);
    try {
      await timeAndLeaveGateway.accrueForPeriod(employeeId, leavePlanId, buildMonthlyAccrualRequest(new Date()));
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not accrue leave for this period.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error && <p className="error">{error}</p>}
      <label htmlFor="balance-plan-select">Leave plan</label>
      <select id="balance-plan-select" value={leavePlanId} onChange={(e) => setLeavePlanId(e.target.value)}>
        {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({leavePlanTypeLabels[p.planType]})</option>)}
      </select>

      <p>
        Enrollment: {isEnrolled ? <span className="success">Enrolled</span> : <span className="muted">Not enrolled</span>}
        {!isEnrolled && <> <button type="button" disabled={busy} onClick={() => void handleEnroll()}>Enroll</button></>}
      </p>

      {balance ? (
        <div className="stat-grid">
          <div className="stat-card"><span className="stat-value">{balance.availableLeaves}</span><span className="muted">Available hours</span></div>
          <div className="stat-card"><span className="stat-value">{balance.accruedLeaves}</span><span className="muted">Accrued (last entry)</span></div>
          <div className="stat-card"><span className="stat-value">{balance.leavesTaken}</span><span className="muted">Taken (last entry)</span></div>
        </div>
      ) : (
        <p className="muted">No balance calculated yet for this plan.</p>
      )}

      <button type="button" disabled={busy || !isEnrolled} onClick={() => void handleAccrue()}>
        {busy ? 'Working…' : 'Accrue for current pay period'}
      </button>
    </div>
  );
}

function HolidaysPanel({ employeeId }: { employeeId: string }) {
  const [holidays, setHolidays] = useState<HolidayResponse[]>([]);
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [holidayDate, setHolidayDate] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, assignments] = await Promise.all([
        timeAndLeaveGateway.listHolidays(),
        timeAndLeaveGateway.listEmployeeHolidayAssignments(employeeId),
      ]);
      setHolidays(list.sort((a, b) => a.holidayDate.localeCompare(b.holidayDate)));
      setAssignedIds(new Set(assignments.map((a) => a.holidayDefinitionId)));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load the holiday calendar.'));
    }
  }, [employeeId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await timeAndLeaveGateway.createHoliday({ name, holidayDate, isFixed: false, defaultWorkHourCredit: 8, defaultLeaveHourCredit: null });
      setName('');
      setHolidayDate('');
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not create the holiday.'));
    }
  }

  async function handleAssign(holidayId: string) {
    setBusyId(holidayId);
    setError(null);
    try {
      await timeAndLeaveGateway.assignHoliday(employeeId, holidayId);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not assign the holiday.'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      {error && <p className="error">{error}</p>}

      <form className="inline-form" onSubmit={handleCreate} style={{ margin: '12px 0' }}>
        <input placeholder="Holiday name" value={name} onChange={(e) => setName(e.target.value)} required />
        <label className="inline-form-date-label">
          Holiday date
          <input type="date" value={holidayDate} onChange={(e) => setHolidayDate(e.target.value)} required />
        </label>
        <button type="submit">Add company holiday</button>
      </form>

      <table>
        <thead><tr><th>Date</th><th>Name</th><th>Scope</th><th></th></tr></thead>
        <tbody>
          {holidays.map((h) => (
            <tr key={h.id}>
              <td>{h.holidayDate}</td>
              <td>{h.name}</td>
              <td>{h.isGlobal ? 'Global' : 'Company'}</td>
              <td>
                {assignedIds.has(h.id) ? (
                  <span className="success">Assigned to this employee</span>
                ) : (
                  <button type="button" className="link" disabled={busyId === h.id} onClick={() => void handleAssign(h.id)}>Assign to this employee</button>
                )}
              </td>
            </tr>
          ))}
          {holidays.length === 0 && <tr><td colSpan={4} className="muted">No holidays defined yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Local-midnight parse of a `YYYY-MM-DD` string — avoids the UTC-parse day-shift pitfall `new
 * Date(isoString)` has in negative-UTC-offset time zones (same pattern as `formatReportDate` elsewhere in
 * this app). */
function parseIsoDate(iso: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return new Date(NaN);
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

/** Every calendar date from `startIso` to `endIso` inclusive, as `YYYY-MM-DD` strings. Returns `[]` if the
 * range is invalid (end before start) rather than throwing — the caller decides how to surface that. */
function enumerateDates(startIso: string, endIso: string): string[] {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (end < start) return [];
  const dates: string[] = [];
  for (let d = start; d <= end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    dates.push(toIsoDate(d));
  }
  return dates;
}

function employeeDisplayName(e: EmployeeResponse | undefined): string | undefined {
  return e ? `${e.firstName} ${e.lastName}`.trim() : undefined;
}

/**
 * EPN-82 — ADMIN-ASSISTED employee leave experience (preview). An admin/manager picks the target employee
 * with the same employee-picker every other tab on this page uses, and this panel shows and acts on that
 * employee's leave. It is NOT true employee self-service: no User↔Employee identity link exists anywhere in
 * EPN yet and the seeded Employee role has zero permission grants, so an Employee login cannot use this
 * screen (see docs/time-off.md). Deliberately shows only the self-serve-shaped actions (view balances,
 * submit a request, cancel a still-Submitted request) — Approve/Reject stay on the Requests tab, which is
 * the manager-facing surface.
 * <para>
 * Submission supports a real date range (building the same `Days[]` the API already accepts), unlike the
 * Requests tab's single-day-only form — multi-day is part of the real model, not a new rule. No client-side
 * balance or overlap check is added: the backend doesn't enforce either today (confirmed by reading
 * LeaveRequestService.CreateAsync), so adding one here would be inventing a rule the API doesn't have. The
 * fetched balance is shown as information above the form instead.
 * <para>
 * Async isolation: the caller keys this component on `employeeId`, so a selection change remounts it with
 * fresh state. On top of that, every load is guarded by an AbortController plus a generation counter, so a
 * response (or failure) that resolves after the employee changed — or after a newer load began — is
 * discarded and can never overwrite what is shown for the currently selected employee. Enrollments,
 * balances and requests are committed together only when the whole load succeeds, so a partial failure
 * cannot leave a half-populated view.
 */
function EmployeeExperiencePanel({ employeeId, plans, employeeName }: { employeeId: string; plans: LeavePlanResponse[]; employeeName?: string | undefined }) {
  const [enrollments, setEnrollments] = useState<LeavePlanEnrollmentResponse[]>([]);
  const [balances, setBalances] = useState<Record<string, LeaveBalanceResponse | null>>({});
  const [requests, setRequests] = useState<LeaveRequestResponse[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [leavePlanId, setLeavePlanId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('8');
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const generation = useRef(0);
  const inFlight = useRef<AbortController | null>(null);

  const possessive = employeeName ? `${employeeName}'s` : "this employee's";
  const enrolledPlans = plans.filter((p) => enrollments.some((e) => e.leavePlanId === p.id && e.isActive));

  const load = useCallback(async () => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    const myGeneration = ++generation.current;
    const isCurrent = () => myGeneration === generation.current;

    setLoading(true);
    setError(null);
    try {
      const [enr, reqs] = await Promise.all([
        timeAndLeaveGateway.listEnrollments(employeeId, controller.signal),
        timeAndLeaveGateway.listLeaveRequests(employeeId, controller.signal),
      ]);
      const activePlanIds = enr.filter((e) => e.isActive).map((e) => e.leavePlanId);
      const balanceEntries = await Promise.all(
        activePlanIds.map(async (planId) => [planId, await timeAndLeaveGateway.getLeaveBalance(employeeId, planId, controller.signal)] as const),
      );
      if (!isCurrent()) return;
      setEnrollments(enr);
      setRequests([...reqs].sort((a, b) => b.createdAtUtc.localeCompare(a.createdAtUtc)));
      setBalances(Object.fromEntries(balanceEntries));
      setLoaded(true);
    } catch (err) {
      if (!isCurrent()) return;
      setError(friendlyErrorMessage(err, `Could not load ${possessive} leave balances and requests.`));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [employeeId, possessive]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
    return () => {
      // Unmount (or employee/identity change): invalidate any outstanding load so its result is discarded.
      generation.current += 1;
      inFlight.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    const first = enrolledPlans[0];
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (first) setLeavePlanId((prev) => (enrolledPlans.some((p) => p.id === prev) ? prev : first.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollments, plans]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const dates = enumerateDates(startDate, endDate || startDate);
    if (dates.length === 0) {
      setSubmitError('End date must be on or after the start date.');
      return;
    }

    setSubmitting(true);
    try {
      await timeAndLeaveGateway.createLeaveRequest(employeeId, {
        leavePlanId,
        reason: reason || null,
        days: dates.map((leaveDate) => ({ leaveDate, hours: Number(hoursPerDay) })),
      });
      setStartDate('');
      setEndDate('');
      setReason('');
      await load();
    } catch (err) {
      setSubmitError(friendlyErrorMessage(err, 'Could not submit the leave request.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel(requestId: string) {
    setBusyId(requestId);
    setError(null);
    try {
      await timeAndLeaveGateway.cancelLeaveRequest(requestId);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not cancel this request.'));
    } finally {
      setBusyId(null);
    }
  }

  const notice = (
    <p className="employee-experience__notice muted" role="note">
      <span className="badge">Admin-assisted preview</span>{' '}
      You are viewing and acting on behalf of {employeeName ?? 'the selected employee'}. This is not yet an
      Employee self-service portal — employee sign-in access to this screen is not available.
    </p>
  );

  if (loading && !loaded) {
    return <p className="muted" role="status">Loading {possessive} leave information…</p>;
  }

  if (!loaded) {
    // The first load for this employee failed: show only the error — never an empty "not enrolled" view
    // that would misrepresent a failed load as real data.
    return (
      <div className="employee-experience">
        {notice}
        {error && <p className="error" role="alert">{error}</p>}
      </div>
    );
  }

  return (
    <div className="employee-experience" aria-busy={loading}>
      {notice}
      {error && <p className="error" role="alert">{error}</p>}

      <h3>{possessive} Leave Balances</h3>
      {enrolledPlans.length === 0 ? (
        <p className="muted">Not enrolled in any leave plan yet. Enroll this employee on the Plans &amp; Balances tab first.</p>
      ) : (
        <div className="stat-grid">
          {enrolledPlans.map((plan) => {
            const balance = balances[plan.id];
            return (
              <div className="stat-card" key={plan.id}>
                <span className="stat-value">{balance ? balance.availableLeaves : '—'}</span>
                <span className="muted">{plan.name} available</span>
              </div>
            );
          })}
        </div>
      )}

      <h3>Request Time Off</h3>
      {enrolledPlans.length === 0 ? (
        <p className="muted">A leave request can only be submitted for a plan this employee is enrolled in.</p>
      ) : (
        <form className="inline-form employee-experience__form" onSubmit={handleSubmit}>
          {submitError && <p className="error" role="alert">{submitError}</p>}
          <label>
            Leave plan
            <select value={leavePlanId} onChange={(e) => setLeavePlanId(e.target.value)} required>
              {enrolledPlans.map((p) => <option key={p.id} value={p.id}>{p.name} ({leavePlanTypeLabels[p.planType]})</option>)}
            </select>
          </label>
          <label>
            Start date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </label>
          <label>
            End date (optional — defaults to start date)
            {/* Deliberately no `min={startDate}` here: a native min/max constraint would make the browser
                silently block form submission (no "submit" event at all) for an end-before-start mismatch,
                bypassing the friendly error message below entirely and making the two validations disagree
                across browsers. The explicit check in handleSubmit is the single source of truth instead. */}
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <label>
            Hours per day
            <input type="number" step="0.25" min="0" value={hoursPerDay} onChange={(e) => setHoursPerDay(e.target.value)} required />
          </label>
          <label>
            Reason (optional)
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <button type="submit" disabled={submitting || !leavePlanId || !startDate}>
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
        </form>
      )}

      <h3>{possessive} Requests</h3>
      {requests.length === 0 ? (
        <p className="muted">No leave requests yet.</p>
      ) : (
        <ul className="employee-experience__request-list">
          {requests.map((r) => (
            <li className="employee-experience__request-card" key={r.id}>
              <div className="employee-experience__request-card-header">
                <span className="badge">{leaveRequestStatusLabels[r.status]}</span>
                <span className="muted">{r.days.length} day{r.days.length === 1 ? '' : 's'}</span>
              </div>
              <p>{r.days.map((d) => `${d.leaveDate} (${d.hours}h)`).join(', ')}</p>
              {r.reason && <p className="muted">Reason: {r.reason}</p>}
              {r.status === LeaveRequestStatus.Rejected && r.rejectionReason && (
                <p className="muted">Rejection reason: {r.rejectionReason}</p>
              )}
              {r.status === LeaveRequestStatus.Submitted && (
                <button type="button" className="link" disabled={busyId === r.id} onClick={() => void handleCancel(r.id)}>
                  {busyId === r.id ? 'Cancelling…' : 'Cancel request'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
